// Static registry of "Claude AI Developer V3" (CD03) course lessons.
//
// V3 is a SEPARATE course from CD01 and CD02, not a modification of either. Both
// stay live and untouched; nobody mid-course is disturbed and no progress is
// migrated. Its lesson IDs and routes are numbered in true display order.
//
// What V3 changes versus CD02:
//
//   CD02's promise is "build a real web app by directing Claude", but its Module 3
//   breaks that promise — nine lessons of variables, if/else, loops, functions and
//   events, which is an intro-to-programming course wearing an HTML costume. A
//   complete beginner who wants an online menu or a booking page stalls on
//   "Variables and data in JavaScript".
//
//   V3 changes what Module 3 is FOR. It stops teaching people to WRITE code and
//   starts teaching them to READ, RECOGNISE and PLACE it — enough vocabulary to say
//   "the button is in the HTML part, the colour is in the CSS part" and tell Claude
//   precisely what they want. Design rules for that module:
//
//     1. One idea per lesson. Never two concepts in the same lesson.
//     2. Tiny edits only. The learner changes a single value, word or colour on a
//        page that already works; anything longer than one line they ask Claude for.
//     3. Recognition over authorship. Success is "I know which part to point at".
//     4. Every lesson ends on a visible win — press Run, see the page change.
//
//   Concretely: 9 lessons become 8 (CSS layout is cut outright — it is the single
//   most confusing beginner topic and Claude does it perfectly on request; loops,
//   functions and events collapse into recognition-level lessons), and CD02's
//   V2106 "The three languages of the web" leaves Module 1 because the new Module 3
//   IS that lesson, taught hands-on.
//
// 36 lessons vs. CD02's 38.
//
// `layout` decides which lesson layout renders (UI only):
//   "canvas"    — Lesson | (Concept Canvas + Prompt Lab).
//   "promptLab" — Lesson | Prompt Lab | Editor or Rubric panel.
//   "template"  — Lesson | (brand form + live Starter App Template preview).
//   "webLab"    — Lesson | HTML editor | live page preview + checks.
//
// `grading` decides how the lesson is assessed (authoritative; decoupled from layout):
//   "canvas"   — interactive self-check, no AI grade.
//   "rubric"   — the student's conversation is graded against a rubric by an AI judge.
//   "template" — self-check: they branded the Starter App Template and ran it.
//   "dom"      — assertions run against the rendered page inside the preview iframe.
//
// `captureProject: true` adds the "My App Idea" card to the Prompt Lab, which
// saves the student's own app to `users_projects`. From then on every lesson chat
// in the course is personalized to it (see promptLabClaudeController).
//
// `lessonID` MUST match the Firestore `lessons` document ID — it keys the
// per-user content cache. V3 ids are distinct from CD01's and CD02's, so all
// three courses track progress and cache content completely independently.
//
// DEF_VERSION: bump whenever the generated lesson schema or grading changes, so
// per-user cached definitions are treated as stale and regenerated on next load.
//
// It starts at 4, NOT 1. `CURRENT_DEF_VERSIONS` in backend/src/services/userService.js
// is a single global Set shared by every course (CD01 = 2, CD02 = 3), so version
// numbers live in one namespace. A `1` in that set would make stale CD01 content
// still carrying defVersion 1 start passing the freshness gate.
export const DEF_VERSION = 4;

export const CLAUDE_DEV_V3_COURSE_LESSONS = [
    // Module 1 — Your idea, and your first win (day one)
    { lessonID: "V3101", route: "V3_M1_L1", title: "Choose your idea: what do you want to build?", layout: "promptLab", type: "concept", grading: "rubric", captureProject: true },
    { lessonID: "V3102", route: "V3_M1_L2", title: "Your app, running today: make the Starter App Template yours", layout: "template", type: "concept", grading: "template" },

    // …then Foundations: How Software Actually Works (interactive canvas self-check).
    // CD02's "three languages of the web" lesson is deliberately absent — Module 3
    // teaches exactly that, on a live page, where it actually lands.
    { lessonID: "V3103", route: "V3_M1_L3", title: "What software is and what a developer actually does", layout: "canvas", type: "concept", grading: "canvas" },
    { lessonID: "V3104", route: "V3_M1_L4", title: "How a program runs (input → logic → output)", layout: "canvas", type: "concept", grading: "canvas" },
    { lessonID: "V3105", route: "V3_M1_L5", title: "Frontend vs. backend vs. database — the three-part picture", layout: "canvas", type: "concept", grading: "canvas" },
    { lessonID: "V3106", route: "V3_M1_L6", title: "The developer's toolkit: editor, terminal, browser", layout: "canvas", type: "concept", grading: "canvas" },
    { lessonID: "V3107", route: "V3_M1_L7", title: "The single most important skill: breaking a problem into steps", layout: "canvas", type: "concept", grading: "canvas" },

    // Module 2 — Working With Claude as a Developer (conversation graded by rubric)
    { lessonID: "V3201", route: "V3_M2_L1", title: "What Claude can and can't do for a developer", layout: "promptLab", type: "concept", grading: "rubric" },
    { lessonID: "V3202", route: "V3_M2_L2", title: "Anatomy of a good coding prompt (context, goal, constraints)", layout: "promptLab", type: "concept", grading: "rubric" },
    { lessonID: "V3203", route: "V3_M2_L3", title: "Reading Claude's response: code, explanation, and assumptions", layout: "promptLab", type: "concept", grading: "rubric" },
    { lessonID: "V3204", route: "V3_M2_L4", title: "Iterating: \"that's close, now change this\"", layout: "promptLab", type: "concept", grading: "rubric" },
    { lessonID: "V3205", route: "V3_M2_L5", title: "Giving Claude an error message and getting unstuck", layout: "promptLab", type: "concept", grading: "rubric" },
    { lessonID: "V3206", route: "V3_M2_L6", title: "Asking Claude to explain code you don't understand", layout: "promptLab", type: "concept", grading: "rubric" },
    { lessonID: "V3207", route: "V3_M2_L7", title: "When to trust the AI and when to verify", layout: "promptLab", type: "concept", grading: "rubric" },

    // ★ Module 3 — Reading the code Claude writes for you (live preview, DOM checks)
    // Recognition, not authorship. The learner never starts from a blank file: every
    // lesson opens on a page that already works and asks for ONE change. Vocabulary
    // is the real deliverable — tag, rule, click, value, list, decision are the words
    // they will use with Claude for the rest of the course and afterwards.
    { lessonID: "V3301", route: "V3_M3_L1", title: "Your first look at real code — and your first edit", layout: "webLab", type: "concept", grading: "dom" },
    { lessonID: "V3302", route: "V3_M3_L2", title: "HTML: the file that holds everything you see", layout: "webLab", type: "concept", grading: "dom" },
    { lessonID: "V3303", route: "V3_M3_L3", title: "CSS: the rules that decide how it looks", layout: "webLab", type: "concept", grading: "dom" },
    // "JavaScript:" below is this title's prefix, matching "HTML:" and "CSS:" above.
    // no-script-url reads it as a javascript: URL scheme. The three parallel titles
    // are deliberate, so silence the rule rather than reword one of them.
    // eslint-disable-next-line no-script-url
    { lessonID: "V3304", route: "V3_M3_L4", title: "JavaScript: the part that makes something happen", layout: "webLab", type: "concept", grading: "dom" },
    // No typing at all — the "how it's all connected" lesson, and the reason the
    // module can afford to stay this light everywhere else.
    { lessonID: "V3305", route: "V3_M3_L5", title: "How the three fit together — and where a change goes", layout: "canvas", type: "concept", grading: "canvas" },
    { lessonID: "V3306", route: "V3_M3_L6", title: "What your app remembers: values and lists", layout: "webLab", type: "concept", grading: "dom" },
    { lessonID: "V3307", route: "V3_M3_L7", title: "What your app decides: if this, then that", layout: "webLab", type: "concept", grading: "dom" },
    // The transfer lesson: the point is describing the problem to Claude, not
    // debugging alone.
    { lessonID: "V3308", route: "V3_M3_L8", title: "Something looks wrong: read it, tell Claude, fix it", layout: "webLab", type: "concept", grading: "dom" },

    // Module 4 — Build your app (planning → rubric, building → live page + DOM checks)
    // The project was already chosen in Module 1, so this opens on specifying it.
    // The build lessons grow the SAME app they branded in V3102 — see
    // starterTemplate/authoredLessons.js, which supplies each lesson's starting point.
    // The other half of V3101 — graded against what Module 1 already settled as well
    // as its own conversation, so it works on the parts of a spec the student hasn't
    // pinned down yet (components/PromptLabHub/buildSpecLesson.js). `downloadOnPerfect`
    // rewards a full slate with their branded app, spec included.
    { lessonID: "V3401", route: "V3_M4_L1", title: "Turning your idea into a build spec with Claude", layout: "promptLab", type: "concept", grading: "rubric", downloadOnPerfect: true },
    { lessonID: "V3402", route: "V3_M4_L2", title: "Planning the pieces: pages, data and actions", layout: "promptLab", type: "concept", grading: "rubric" },
    // `downloadable: true` turns on the Web Lab "Download my app" button, which saves the
    // student's HTML as index.html so they can keep building their app locally with Claude.
    { lessonID: "V3403", route: "V3_M4_L3", title: "Building your app's main screen", layout: "webLab", type: "concept", grading: "dom", downloadable: true },
    { lessonID: "V3404", route: "V3_M4_L4", title: "Making it interactive: buttons and forms", layout: "webLab", type: "concept", grading: "dom", downloadable: true },
    { lessonID: "V3405", route: "V3_M4_L5", title: "Saving your data so it's still there tomorrow", layout: "webLab", type: "concept", grading: "dom", downloadable: true },
    { lessonID: "V3406", route: "V3_M4_L6", title: "Showing your saved data as a list", layout: "webLab", type: "concept", grading: "dom", downloadable: true },
    { lessonID: "V3407", route: "V3_M4_L7", title: "Adding an AI feature to your app", layout: "webLab", type: "concept", grading: "dom", downloadable: true },
    { lessonID: "V3408", route: "V3_M4_L8", title: "Testing each piece as you go", layout: "promptLab", type: "concept", grading: "rubric" },

    // Module 5 — Shipping, Debugging & Growing (reflection / process graded by rubric)
    { lessonID: "V3501", route: "V3_M5_L1", title: "Version control basics with Git (using Claude to learn it)", layout: "promptLab", type: "concept", grading: "rubric" },
    { lessonID: "V3502", route: "V3_M5_L2", title: "When things break: a calm debugging workflow", layout: "promptLab", type: "concept", grading: "rubric" },
    // Their customers are on phones, so this comes BEFORE publishing. CD02 used to
    // have the same ordering, numbered V2506; its Module 5 has since been rebuilt as
    // a Git → GitHub → GitHub Pages module and no longer has this lesson at all.
    { lessonID: "V3503", route: "V3_M5_L3", title: "Making your app work on a phone", layout: "webLab", type: "concept", grading: "dom", downloadable: true },
    { lessonID: "V3504", route: "V3_M5_L4", title: "Putting your app online so others can use it", layout: "promptLab", type: "concept", grading: "rubric" },
    { lessonID: "V3505", route: "V3_M5_L5", title: "Reading documentation with AI as your translator", layout: "promptLab", type: "concept", grading: "rubric" },
    { lessonID: "V3506", route: "V3_M5_L6", title: "Where to go next and how to keep learning", layout: "promptLab", type: "concept", grading: "rubric" },
];

// Every lesson carries the course's DEF_VERSION so consumers can check a cached
// definition against the version of the course it belongs to. PromptLabHubComponent
// is shared by CD01, CD02 and CD03, which version independently — it must not
// assume any one course's constant.
CLAUDE_DEV_V3_COURSE_LESSONS.forEach((l) => {
    l.defVersion = DEF_VERSION;
});

export const getV3LessonMetaByRoute = (route) =>
    CLAUDE_DEV_V3_COURSE_LESSONS.find((l) => l.route === route);

/**
 * Given a route, return the route of the next lesson in the registry, or null
 * if this is the last one.
 */
export const getNextV3LessonRoute = (currentRoute) => {
    const idx = CLAUDE_DEV_V3_COURSE_LESSONS.findIndex((l) => l.route === currentRoute);
    if (idx === -1 || idx === CLAUDE_DEV_V3_COURSE_LESSONS.length - 1) return null;
    return CLAUDE_DEV_V3_COURSE_LESSONS[idx + 1].route;
};

/**
 * Given a route, return the route of the previous lesson in the registry, or null
 * if this is the first one (the footer's Back button then falls back to the ToC).
 */
export const getPrevV3LessonRoute = (currentRoute) => {
    const idx = CLAUDE_DEV_V3_COURSE_LESSONS.findIndex((l) => l.route === currentRoute);
    if (idx <= 0) return null;
    return CLAUDE_DEV_V3_COURSE_LESSONS[idx - 1].route;
};

/**
 * Position of a lesson within its module, for a "Lesson X of Y" progress label.
 * The module number is parsed from the V3_M{n}_L{k} route and the total counts
 * lessons sharing that module. Returns null for unknown/non-V3 routes.
 */
export const getV3LessonProgress = (route) => {
    const m = /^V3_M(\d+)_L/.exec(route || "");
    if (!m) return null;
    const moduleNum = Number(m[1]);
    const moduleLessons = CLAUDE_DEV_V3_COURSE_LESSONS.filter((l) =>
        l.route.startsWith(`V3_M${moduleNum}_L`)
    );
    const lessonInModule = moduleLessons.findIndex((l) => l.route === route) + 1;
    if (lessonInModule === 0) return null;
    return { moduleNum, lessonInModule, totalInModule: moduleLessons.length };
};
